import React from 'react';
import ReactTooltip from 'react-tooltip';
import {
	BaasicDropdown,
	DatePicker,
	BaasicHeader,
	BaasicButton,
	Loader,
	BaasicInput,
	BaasicCheckbox,
	BaasicDropzone,
} from 'core/components';
import { defaultTemplate } from 'core/hoc';
import moment from 'moment';
import _ from 'lodash';
import { ShipmentCostList, ShipmentCostChargeList } from 'modules/shipment-cost/pages';
import { observer } from 'mobx-react';
import { ThreeStateToggle } from 'common/utils';
import { shipmentInfoTypeEnum } from 'common/constants';

const ShipmentCostTemplate = ({
	t,
	shipmentCostViewStore,
	shipmentCostViewStore: { form, loading, onTrackingNumberSearch, onValueChange, chargeTypeFilter, onChargeTypeRemove },
}) => {
	if (loading) return <Loader />;
	return (
		<React.Fragment>
			<BaasicHeader title="MAINMENU.SHIPMENTCOST.TITLE"></BaasicHeader>
			<div className="container">
				<div className="shipment-cost">
					<div className="shipment-cost__main">
						<TotalsAndExport viewStore={shipmentCostViewStore} t={t} />

						<div className="box--primary box--sml spc--bottom--sml">
							<ShipmentCostChargeList />
						</div>

						<div className="box--primary box--sml spc--bottom--sml">
							{chargeTypeFilter != null && chargeTypeFilter.name != '' && (
								<button className="btn btn--med spc--right--med" type="button" onClick={onChargeTypeRemove}>
									<p>{chargeTypeFilter.name}</p>
									<i className="fa fa-times spc--left--sml" />
								</button>
							)}
							<div className="w--330--px align--h--right spc--bottom--sml">
								<BaasicInput
									inputClassName="spc--right--sml"
									innerClassName="input--single-row"
									placeholder={t('SEARCHTRACKINGNUMBER')}
									field={form.$('searchTrackingNumber')}
									onIconClick={onTrackingNumberSearch}
									iconClass={'fa fa-search k-custom-clickable'}
									onKeyUp={onValueChange}
									className="input--no-label--first-child"
									onClear={() => {
										form.$('searchTrackingNumber').value = '';
										onTrackingNumberSearch();
									}}
								/>
							</div>
							<ShipmentCostList />
						</div>
					</div>

					<AsideSearchUploadDownload viewStore={shipmentCostViewStore} t={t} />
				</div>
			</div>
		</React.Fragment>
	);
};

const TotalsAndExport = observer(({ viewStore, t }) => {
	const {
		shipmentCostTotals,
		form,
		onUnderchargeClick,
		onOverchargeClick,
		onCheckDiscrepancies,
		onCheck,
		onExport,
		isExportDisabled,
		rootStore,
		validateFormAndFind,
	} = viewStore;
	return (
		<React.Fragment>
			<div className="shipment-cost__summary">
				<div className="shipment-cost__summary__item default">
					<label>{t('SHIPMENTCOST.QUOTEDRATE')}</label>
					<p>{rootStore.usdFormatter.format(shipmentCostTotals.totalQuoted)}</p>
				</div>
				<div className="shipment-cost__summary__item default">
					<label>{t('SHIPMENTCOST.BILLEDRATE')}</label>
					<p>{rootStore.usdFormatter.format(shipmentCostTotals.totalBilled)}</p>
				</div>
				<button
					className={`shipment-cost__summary__item undercharged ${
						form.$('showOnlyUndercharged').value ? 'is-active' : ''
					}`}
					type="button"
					onClick={() => onUnderchargeClick()}
				>
					<label>{t('SHIPMENTCOST.UNDERCHARGED')}</label>
					<p>
						{shipmentCostTotals.totalUndercharged > 0
							? `+${rootStore.usdFormatter.format(shipmentCostTotals.totalUndercharged)}`
							: rootStore.usdFormatter.format(shipmentCostTotals.totalUndercharged)}
					</p>
				</button>
				<button
					className={`shipment-cost__summary__item overcharged ${
						form.$('showOnlyOvercharged').value ? 'is-active' : ''
					}`}
					type="button"
					onClick={() => onOverchargeClick()}
				>
					<label>{t('SHIPMENTCOST.OVERCHARGED')}</label>
					<p>
						{shipmentCostTotals.totalOvercharged > 0
							? `+${rootStore.usdFormatter.format(shipmentCostTotals.totalOvercharged)}`
							: rootStore.usdFormatter.format(shipmentCostTotals.totalOvercharged)}
					</p>
				</button>
				<div className="shipment-cost__summary__item difference">
					<label>{t('SHIPMENTCOST.DIFFERENCE')}</label>
					<p>
						{shipmentCostTotals.totalDifference > 0
							? `+${rootStore.usdFormatter.format(shipmentCostTotals.totalDifference)}`
							: rootStore.usdFormatter.format(shipmentCostTotals.totalDifference)}
					</p>
				</div>
			</div>

			<div className="flex flex--secondary spc--bottom--sml--alt">
				<BaasicButton
					className="btn btn--med btn--primary btn--primary"
					type="button"
					label="SHIPMENTCOST.EXPORT"
					disabled={isExportDisabled}
					onClick={() => onExport()}
				/>
				<div className="flex--primary">
					<div className="spc--right--sml">
						<BaasicCheckbox
							field={form.$('showOnlyDiscrepancies')}
							onChangeCallback={event => onCheckDiscrepancies(event, form.$('showOnlyDiscrepancies').path)}
						/>
					</div>
					<div>
						<BaasicCheckbox
							field={form.$('showOnlyWithBilling')}
							onChangeCallback={async () => await validateFormAndFind()}
						/>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
});

const AsideSearchUploadDownload = observer(({ viewStore, t }) => {
	const {
		form,
		datePickerFormat,
		onFilesDrop,
		downloadTemplate,
		shipmentCostTotals,
		shipmentTypeFilter,
	} = viewStore;
	var uploadType = '.xls, .xlsx';
	return (
		<div className="shipment-cost__aside">
			<div className="spc--bottom--med">
				<DatePicker
					field={form.$('dateRangeStart')}
					maxDate={moment(form.$('dateRangeEnd').value, datePickerFormat).toDate()}
					rootStore={viewStore.rootStore}
					isFilter={true}
					format={datePickerFormat}
				/>
			</div>
			<div className="spc--bottom--med">
				<DatePicker
					field={form.$('dateRangeEnd')}
					minDate={moment(form.$('dateRangeStart').value, datePickerFormat).toDate()}
					rootStore={viewStore.rootStore}
					isFilter={true}
					format={datePickerFormat}
				/>
			</div>

			<div className="spc--bottom--med">
				<ThreeStateToggle
					t={t}
					threeState={shipmentTypeFilter}
					first={{ value: shipmentInfoTypeEnum.shipment, translation: 'SHIPMENTCOST.THREESTATE.SHIPMENTS' }}
					second={{ value: shipmentInfoTypeEnum.return, translation: 'SHIPMENTCOST.THREESTATE.RETURNS' }}
					third={{ value: null, translation: 'SHIPMENTCOST.THREESTATE.BOTH' }}
				/>
			</div>

			<button className="btn btn--med btn--primary spc--bottom--med" type="submit" onClick={e => form.onSubmit(e)}>
				{t('SEARCH')}
			</button>

			<div className="dropzone spc--bottom--med">
				<BaasicDropzone className="w--100" acceptFiles={uploadType} onFilesDrop={onFilesDrop} />
				<p className="type--center">{t('INVENTORYSYNC.EXCEL')}</p>
			</div>

			<div className="shipment-cost__card shipment-cost__card--template-card spc--bottom--med">
				<p className="spc--bottom--xsml">{t('SHIPMENTCOST.DOWNLOADTEMPLATEINFO')}</p>
				<BaasicButton
					className="btn btn--med btn--outline w--100"
					onClick={() => downloadTemplate(false)}
					icon="icon icon--xmed icon--arrow-down spc--right--sml"
					label="SHIPMENTCOST.DOWNLOADTEMPLATE"
				/>
			</div>

			{shipmentCostTotals.missingTrackingNumbers != null && shipmentCostTotals.missingTrackingNumbers.length > 0 && (
				<div className="shipment-cost__card shipment-cost__card--error">
					<div>
						<label className="spc--bottom--xsml">{t('SHIPMENTCOST.MISSINGBILLED')}</label>
						<p className="type--lrg type--wgt--bold">{shipmentCostTotals.missingTrackingNumbers.length}</p>
					</div>
					<BaasicButton
						className="btn btn--med btn--outline--warning"
						onClick={() => downloadTemplate(true)}
						icon="icon icon--xmed icon--arrow-down--warning spc--right--sml"
						label="SHIPMENTCOST.DOWNLOAD"
					/>
				</div>
			)}

			<ReactTooltip />
		</div>
	);
});

export default defaultTemplate(ShipmentCostTemplate);
